import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Icon, Popover, List, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import waitForStoreUpdate from '@utils/waitForStoreUpdate';
import { logoWhite } from '@assets';
import {
  faUser,
  faFileAlt,
  faMailbox,
  faHandshakeAlt,
  faKey,
  faCog,
  // faDatabase,
  // faUserSlash,
  faFile,
  faGlobe,
  faPaperPlane,
  faClock,
  faPlayCircle,
  faFileEdit,
  faQuestionCircle,
  faTasks,
  faLink,
  faCommentAltLines,
  faExclamationTriangle,
  faBell,
  faBox,
  faBooks,
  faGripHorizontal,
  faFlag,
  faCalendarStar,
  faCircleNodes,
  faHospitals,
  faHospital,
  faDatabase,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { procedurePathway } from '@assets/icons';
import { logout, goToPlatformApp } from '@redux/login/actions';
import { hasValidSession, selectCurrentDashboardUser } from '@redux/login/reducer';
import MenuItem from './MenuItem';
import LanguageSelector from './LanguageSelectorPopup';
import { Permissions } from '@authorisation/constants';
import SubMenuItem from './SubMenuItem';
import { useEvaluatePermissions } from '@authorisation/hooks';
import { fetchOrganisation } from '@redux/organisations/actions';
import { selectOrganisation } from '@redux/organisations/reducers';
import { selectHasParentOrganisationCapability } from '@redux/directChildOrganisationAdministration/selectors';
import { useFeature } from '@hooks';
import './style.less';

const { confirm } = Modal;

export default () => {
  const currentDashboardUser = useSelector(selectCurrentDashboardUser);
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 1366);
  const [, organisation] = useSelector(selectOrganisation());
  const hasParentOrganisationCapability = useSelector(selectHasParentOrganisationCapability);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1366 && isCollapsed) {
        setIsCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        window.innerWidth <= 1366 &&
        !isCollapsed &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed]);

  const handleBlur = e => {
    if (window.innerWidth <= 1366) {
      const currentTarget = e.currentTarget;
      if (!currentTarget.contains(document.activeElement)) {
        setIsCollapsed(true);
      }
    }
  };

  const { noTermsTrackingForDashboardUsers } = useFeature('noTermsTrackingForDashboardUsers');
  const { showDataRequestsForHospitalUsers } = useFeature('showDataRequestsForHospitalUsers');

  const doShowCarbon = useEvaluatePermissions(Permissions.AccessCarbon);
  const doShowUbiquity = useEvaluatePermissions(Permissions.AccessUbiquity);

  useEffect(() => {
    dispatch(fetchOrganisation());
  }, [dispatch]);

  const handleVisibleChange = visible => {
    setVisible(visible);
  };

  const showLogOutConfirm = useCallback(() => {
    confirm({
      title: t('logout:title'),
      icon: <FontAwesomeIcon className="log-out-icon" icon={faExclamationCircle} />,
      okText: t('logout:ok'),
      onOk() {
        dispatch(logout());
        return waitForStoreUpdate(store, state => [hasValidSession(state)]);
      },
    });
  }, [dispatch, store, t]);

  return (
    <div
      ref={menuRef}
      className={`menu ${isCollapsed ? 'collapsed' : ''}`}
      onBlur={handleBlur}
      tabIndex={0}
    >
      <div className="logo-container">
        <Icon
          className="collapse-button"
          type="menu-unfold"
          onMouseDown={e => e.preventDefault()}
          onClick={() => setIsCollapsed(false)}
        />
        <div className="logo">
          <img src={logoWhite} alt="" />
        </div>
      </div>
      <div className="menu-items">
        <MenuItem name="Home" path="/" icon={<Icon type="home" />} exact>
          {t('common:Menu.home')}
        </MenuItem>
        <MenuItem
          name="App Users"
          path="/patients/individuals"
          icon={<FontAwesomeIcon icon={faUser} />}
          permissions={[Permissions.ViewPatients]}
        >
          {t('common:Menu.patients.title')}
        </MenuItem>
        <MenuItem
          name="Direct Messaging"
          path="/messages"
          permissions={[Permissions.ViewMessages]}
          icon={<FontAwesomeIcon icon={faMailbox} />}
          subMenuItems={[
            <SubMenuItem
              name="Direct Messaging Sent"
              path="/messages/sent"
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
            >
              {t('common:Menu.messages.sent')}
            </SubMenuItem>,
            <SubMenuItem
              name="Direct Messaging Scheduled"
              path="/messages/scheduled"
              icon={<FontAwesomeIcon icon={faClock} />}
            >
              {t('common:Menu.messages.scheduled')}
            </SubMenuItem>,
          ]}
        >
          {t('common:Menu.messages.title')}
        </MenuItem>
        <MenuItem
          name="Content"
          path="/content"
          icon={<FontAwesomeIcon icon={faFileAlt} />}
          subMenuItems={[
            <SubMenuItem
              name="Documents"
              path="/content/documents"
              icon={<FontAwesomeIcon icon={faFileAlt} />}
            >
              {t('common:Menu.content.documents')}
            </SubMenuItem>,
            <SubMenuItem
              name="Videos"
              path="/content/videos"
              icon={<FontAwesomeIcon icon={faPlayCircle} />}
            >
              {t('common:Menu.content.videos')}
            </SubMenuItem>,
            <SubMenuItem
              name="Forms"
              path="/content/forms"
              icon={<FontAwesomeIcon icon={faFileEdit} />}
            >
              {t('common:Menu.content.forms')}
            </SubMenuItem>,
            <SubMenuItem
              name="Questionnaires"
              path="/content/questionnaires"
              icon={<FontAwesomeIcon icon={faQuestionCircle} />}
            >
              {t('common:Menu.content.questionnaires')}
            </SubMenuItem>,
            <SubMenuItem
              name="Tasks"
              path="/content/tasks"
              icon={<FontAwesomeIcon icon={faTasks} />}
            >
              {t('common:Menu.content.tasks')}
            </SubMenuItem>,
            <SubMenuItem
              name="Weblinks"
              path="/content/weblinks"
              icon={<FontAwesomeIcon icon={faLink} />}
            >
              {t('common:Menu.content.weblinks')}
            </SubMenuItem>,
            <SubMenuItem
              name="Messages"
              path="/content/messages"
              icon={<FontAwesomeIcon icon={faCommentAltLines} />}
            >
              {t('common:Menu.content.messages')}
            </SubMenuItem>,
            <SubMenuItem
              name="Alerts"
              path="/content/alerts"
              icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            >
              {t('common:Menu.content.alerts')}
            </SubMenuItem>,
            <SubMenuItem
              name="Reminders"
              path="/content/reminders"
              icon={<FontAwesomeIcon icon={faBell} />}
            >
              {t('common:Menu.content.reminders')}
            </SubMenuItem>,
          ]}
        >
          {t('common:Menu.content.title')}
        </MenuItem>
        <MenuItem
          name="Pathways heading"
          path="/procedure"
          icon={<Icon className="pathways-menu-item-icon" component={procedurePathway} />}
          subMenuItems={[
            <SubMenuItem
              name="Pathways"
              path="/procedure/pathways"
              icon={<Icon component={procedurePathway} />}
            >
              {t('common:Menu.pathway.pathways')}
            </SubMenuItem>,
            <SubMenuItem
              name="Rules"
              path="/procedure/rules"
              icon={<FontAwesomeIcon icon={faBox} />}
            >
              {t('common:Menu.pathway.rules')}
            </SubMenuItem>,
            <SubMenuItem
              name="SharedPathways"
              path="/procedure/shared-pathways"
              permissions={[Permissions.ViewPathwayLibrary]}
              icon={<FontAwesomeIcon icon={faBooks} />}
            >
              {t('common:Menu.pathway.sharedPathways')}
            </SubMenuItem>,
          ]}
        >
          {t('common:Menu.pathway.title')}
        </MenuItem>

        {/* <MenuItem
          icon={<Icon type="line-chart" />}
          active={pathname.startsWith('/review-hub')}
          onClick={() => trackMainMenu(history, '/review-hub', 'Review Hub heading')}
          subMenuItems={determineMetricsMenuItems()}
        >
          {t('common:Menu.reviewHub.title')}
        </MenuItem> */}

        <MenuItem
          name="Administration heading"
          path="/administration"
          icon={<FontAwesomeIcon icon={faCog} />}
          permissions={[Permissions.ViewAdministation]}
          subMenuItems={[
            <SubMenuItem
              name="Hospitals"
              path="/administration/organisations"
              permissions={[Permissions.AdministerAllHospitals]}
              icon={<FontAwesomeIcon icon={faHospitals} />}
            >
              {t('common:Menu.administration.organisations')}
            </SubMenuItem>,
            <SubMenuItem
              name="Hospital"
              path="/administration/organisation"
              permissions={[Permissions.AdministerHospital]}
              icon={<FontAwesomeIcon icon={faHospital} />}
            >
              {t('common:Menu.administration.organisation')}
            </SubMenuItem>,
            hasParentOrganisationCapability && (
              <SubMenuItem
                name="Child Organisations"
                path="/administration/child-organisations"
                icon={<FontAwesomeIcon icon={faHospitals} />}
              >
                {t('common:Menu.administration.directChildOrganisations')}
              </SubMenuItem>
            ),
            hasParentOrganisationCapability && (
              <SubMenuItem
                name="Child Organisation Users"
                path="/administration/child-organisation-users"
                icon={<FontAwesomeIcon icon={faUsers} />}
              >
                {t('common:Menu.administration.directChildOrganisationUsers')}
              </SubMenuItem>
            ),
            <SubMenuItem
              name="Codes"
              path="/administration/codes"
              permissions={[Permissions.ManageConnectionCodes]}
              icon={<FontAwesomeIcon icon={faGripHorizontal} />}
            >
              {t('common:Menu.administration.codes')}
            </SubMenuItem>,
            <SubMenuItem
              name="Dashboard Users"
              path="/administration/dashboard-users"
              permissions={[Permissions.ViewDashboardUsers]}
              icon={<FontAwesomeIcon icon={faUsers} />}
            >
              {t('common:Menu.administration.dashboard-users')}
            </SubMenuItem>,
            <SubMenuItem
              name="Flags"
              path="/administration/flags"
              permissions={[Permissions.AdministerHospital]}
              icon={<FontAwesomeIcon icon={faFlag} />}
            >
              {t('common:Menu.administration.flags')}
            </SubMenuItem>,
            <SubMenuItem
              name="Index Events"
              path="/administration/index-events"
              permissions={[Permissions.AdministerIndexEvents]}
              icon={<FontAwesomeIcon icon={faCalendarStar} />}
            >
              {t('common:Menu.administration.index')}
            </SubMenuItem>,
            <SubMenuItem
              name="Integrations"
              path="/administration/integrations"
              permissions={[Permissions.AdministerHospital]}
              icon={<FontAwesomeIcon icon={faCircleNodes} />}
            >
              {t('common:Menu.administration.integrations')}
            </SubMenuItem>,
            (currentDashboardUser.role.indexOf('hospital-') === -1 ||
              showDataRequestsForHospitalUsers) && (
              <SubMenuItem
                name="Data Requests"
                path="/administration/data-requests"
                permissions={[Permissions.ViewDataRequests]}
                icon={<FontAwesomeIcon icon={faDatabase} />}
              >
                {t('common:Menu.administration.dataRequests')}
              </SubMenuItem>
            ),
          ]}
        >
          {t('common:Menu.administration.title')}
        </MenuItem>
      </div>
      <Popover
        className="user-details-popover"
        placement="rightBottom"
        title={t('common:Menu.popover.title')}
        trigger={isCollapsed ? 'click' : 'hover'}
        getPopupContainer={triggerNode => triggerNode.parentNode}
        content={
          <List
            className="user-details-popover-list"
            size="small"
            dataSource={[
              {
                icon: <Icon type="lock" />,
                title: t('common:Menu.popover.logout'),
                onClick: () => showLogOutConfirm(),
              },
              {
                icon: <FontAwesomeIcon icon={faUser} />,
                title: t('common:Menu.popover.editProfile'),
                onClick: () => history.push('/my-profile/edit-profile'),
              },
              {
                icon: <FontAwesomeIcon icon={faKey} />,
                title: t('common:Menu.popover.password'),
                onClick: () => history.push('/my-profile/change-password'),
              },
              {
                icon: <FontAwesomeIcon icon={faGlobe} />,
                title: t('common:Menu.popover.language'),
                rightIcon: <Icon type="right" className="change-language-menu" />,
                key: 'languages',
              },
              !noTermsTrackingForDashboardUsers && {
                icon: <FontAwesomeIcon icon={faHandshakeAlt} />,
                title: t('common:Menu.popover.termsnCondition'),
                onClick: () => history.push('/my-profile/terms-and-conditions'),
              },
              !noTermsTrackingForDashboardUsers && {
                icon: <FontAwesomeIcon icon={faFileAlt} />,
                title: t('common:Menu.popover.privacyPolicy'),
                onClick: () => history.push('/my-profile/privacy-policy'),
              },
              // Disable GDPR for staff in PEP for now as typically we have contracts with our customers to handle these concerns.
              // {
              //   icon: <FontAwesomeIcon icon={faDatabase} className="tab-item-icon" />,
              //   title: t('myProfile:Tabs.dataDownload'),
              //   onClick: () => history.push('/my-profile/gdpr-request-data'),
              // },
              // {
              //   icon: <FontAwesomeIcon icon={faUserSlash} className="tab-item-icon" />,
              //   title: t('myProfile:Tabs.dataDeletion'),
              //   onClick: () => history.push('/my-profile/gdpr-delete-data'),
              // },
              doShowCarbon && {
                icon: <FontAwesomeIcon icon={faFile} className="tab-item-icon" />,
                title: t('myProfile:Tabs.carbonEditor'),
                onClick: () => dispatch(goToPlatformApp('carbon')),
              },
              doShowUbiquity && {
                icon: <FontAwesomeIcon icon={faGlobe} className="tab-item-icon" />,
                title: t('myProfile:Tabs.ubiquityDashboard'),
                onClick: () => dispatch(goToPlatformApp('ubiquity')),
              },
            ].filter(item => item)}
            bordered={false}
            split={false}
            renderItem={item => {
              return item.key ? (
                <Popover
                  overlayClassName="change-languages"
                  content={<LanguageSelector />}
                  placement="rightBottom"
                  trigger="hover"
                  visible={visible}
                  onVisibleChange={handleVisibleChange}
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                >
                  <List.Item>
                    {item.icon} {item.title} {item.rightIcon}
                  </List.Item>
                </Popover>
              ) : (
                <List.Item onClick={item.onClick}>
                  {item.icon} {item.title} {item.rightIcon}
                </List.Item>
              );
            }}
          />
        }
      >
        {isCollapsed ? (
          <div className="user-details-collapsed">
            <Icon type="user" />
          </div>
        ) : (
          <div className="user-details">
            <div>
              <Icon type="user" />
              <span className="name">{currentDashboardUser?.name}</span>
              <Icon type="right" />
            </div>
            <div>
              <Icon type="apartment" />
              <span className="name">
                {organisation?.updated_organisation_name || organisation?.organisation_name}
              </span>
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};
